import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, ScrollText, DownloadCloud } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const formatBytes = (n) => `${(n / 1024).toFixed(1)} KB`;

export default function AdminLaws() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("ar");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [syncBase, setSyncBase] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const fileRef = useRef(null);

  const t = useMemo(() => ({
    eyebrow: isAr ? "إدارة القوانين" : "Manage Laws",
    title: isAr ? "ارفع نصوص القوانين الكويتية." : "Upload Kuwaiti statutory texts.",
    lead: isAr
      ? "النصوص المرفوعة هنا تظهر في صفحة «القوانين» العامة، ويستخدمها سامي كمرجع رسمي في إجاباته."
      : "Texts uploaded here appear on the public Laws page and are referenced by Sami as primary source.",
    upload: isAr ? "رفع قانون" : "Upload law",
    upTitle: isAr ? "العنوان" : "Title",
    upLang: isAr ? "اللغة" : "Language",
    upFile: isAr ? "الملف (PDF أو TXT — حتى 100 م.ب.)" : "File (PDF or TXT — up to 100 MB)",
    submit: isAr ? "رفع" : "Upload",
    library: isAr ? "القوانين الحالية" : "Current laws",
    libraryEmpty: isAr ? "لم تُرفع أي قوانين بعد." : "No laws uploaded yet.",
    delete: isAr ? "حذف" : "Delete",
    confirmDelete: isAr ? "حذف هذا القانون؟" : "Delete this law?",
    uploaded: isAr ? "تم الرفع" : "Uploaded",
    err: isAr ? "حدث خطأ. حاول مرة أخرى." : "Something went wrong.",
    syncTitle: isAr ? "استيراد المكتبة من بيئة أخرى" : "Import library from another environment",
    syncLead: isAr
      ? "ألصق رابط /api/laws/export الخاص بـ Preview لنقل كل القوانين إلى هذه البيئة (Production). القوانين الموجودة بالعنوان نفسه تُتجاوز."
      : "Paste the /api/laws/export URL of your Preview deployment to copy all laws into this environment. Laws with the same title are skipped.",
    syncPlaceholder: "https://example.com/api/laws/export",
    syncBtn: isAr ? "استيراد الآن" : "Import now",
    syncing: isAr ? "جارٍ الاستيراد..." : "Importing…",
    syncOk: (n) => isAr ? `تم استيراد ${n} قانون.` : `Imported ${n} laws.`,
    fullSyncTitle: isAr ? "مزامنة شاملة من Preview (قوانين + أبحاث + أحكام)" : "Full sync from Preview (laws + research + judgments)",
    fullSyncLead: isAr
      ? "ألصق رابط البيئة المصدر (مثل https://legal-group-site.preview.emergentagent.com) لاستيراد كل المحتوى (153 قانون + 13 بحث + 19 حكم). يتجاوز الموجود مسبقاً."
      : "Paste the source environment URL (e.g. preview URL) to import all content (laws + research + judgments) into this environment. Existing items are skipped.",
    fullSyncBtn: isAr ? "بدء المزامنة الشاملة" : "Start full sync",
    fullSyncing: isAr ? "جارٍ المزامنة... قد يستغرق دقيقتين" : "Syncing… may take up to 2 minutes",
    fullSyncDone: isAr ? "اكتملت المزامنة" : "Sync complete",
  }), [isAr]);

  const load = useCallback(() => {
    api.get("/laws").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const onUpload = async (e) => {
    e.preventDefault();
    if (!title.trim() || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("language", language);
      fd.append("file", file);
      await api.post("/admin/laws/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success(t.uploaded);
      setTitle("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await api.delete(`/admin/laws/${id}`);
      load();
    } catch {
      toast.error(t.err);
    }
  };

  const onImport = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await api.post(
        "/admin/laws/import-from-url",
        { url: importUrl.trim(), overwrite: false },
        { timeout: 240000 }
      );
      toast.success(t.syncOk(data.inserted ?? 0));
      setImportUrl("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setImporting(false);
    }
  };

  const onFullSync = async (e) => {
    e.preventDefault();
    if (!syncBase.trim()) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await api.post(
        "/admin/sync-from-preview",
        { base_url: syncBase.trim(), overwrite: false },
        { timeout: 300000 }
      );
      setSyncResult(data);
      toast.success(t.fullSyncDone);
      setSyncBase("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="admin-laws-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      <form
        onSubmit={onFullSync}
        className="border border-amber-300 bg-amber-50 p-5 lg:p-6 mb-6 grid md:grid-cols-[1fr_auto] gap-3 items-start"
        data-testid="full-sync-form"
      >
        <div className="md:col-span-2 flex items-center gap-2 mb-1">
          <DownloadCloud className="w-4 h-4 text-amber-700" />
          <p className="eyebrow text-amber-900">{t.fullSyncTitle}</p>
        </div>
        <div className="md:col-span-2 -mt-1 mb-1">
          <p className="text-sm text-slate-700 leading-relaxed">{t.fullSyncLead}</p>
        </div>
        <Input
          type="url"
          value={syncBase}
          onChange={(e) => setSyncBase(e.target.value)}
          placeholder="https://legal-group-site.preview.emergentagent.com"
          data-testid="full-sync-base-url"
          className="rounded-sm h-11 bg-white"
          dir="ltr"
        />
        <Button
          type="submit"
          disabled={syncing || !syncBase.trim()}
          data-testid="full-sync-submit"
          className="rounded-sm h-11 bg-amber-700 hover:bg-amber-800 text-white whitespace-nowrap"
        >
          {syncing ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t.fullSyncing}
            </span>
          ) : t.fullSyncBtn}
        </Button>
        {syncResult ? (
          <div className="md:col-span-2 mt-2 text-xs text-slate-700 grid grid-cols-3 gap-3" data-testid="full-sync-result">
            {["laws", "research", "judgments"].map((k) => {
              const v = syncResult[k];
              if (!v) return null;
              return (
                <div key={k} className="border border-slate-200 bg-white p-3 rounded">
                  <p className="font-display uppercase tracking-[0.15em] text-slate-500 text-[10px] mb-1">{k}</p>
                  {v.error ? (
                    <p className="text-rose-600 text-xs">err: {v.error}</p>
                  ) : (
                    <p>
                      fetched <b>{v.fetched}</b> · inserted <b className="text-emerald-600">{v.inserted}</b> · skipped {v.skipped}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </form>

      <form
        onSubmit={onImport}
        className="border border-slate-200 bg-slate-50 p-5 lg:p-6 mb-8 grid md:grid-cols-[1fr_auto] gap-3 items-start"
        data-testid="laws-import-form"
      >
        <div className="md:col-span-2 flex items-center gap-2 mb-1">
          <DownloadCloud className="w-4 h-4 text-slate-500" />
          <p className="eyebrow">{t.syncTitle}</p>
        </div>
        <div className="md:col-span-2 -mt-1 mb-1">
          <p className="text-sm text-slate-600 leading-relaxed">{t.syncLead}</p>
        </div>
        <Input
          type="url"
          value={importUrl}
          onChange={(e) => setImportUrl(e.target.value)}
          placeholder={t.syncPlaceholder}
          data-testid="laws-import-url"
          className="rounded-sm h-11"
          dir="ltr"
        />
        <Button
          type="submit"
          disabled={importing || !importUrl.trim()}
          data-testid="laws-import-submit"
          className="rounded-sm h-11 bg-slate-900 hover:bg-slate-800 text-white whitespace-nowrap"
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : t.syncBtn}
        </Button>
      </form>

      <div className="grid lg:grid-cols-12 gap-8">
        <form
          onSubmit={onUpload}
          className="lg:col-span-5 border border-slate-200 bg-white p-6 lg:p-8 space-y-5 self-start"
          data-testid="laws-upload-form"
        >
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-slate-500" />
            <p className="eyebrow">{t.upload}</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.upTitle}</Label>
            <Input
              dir="auto" required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="laws-title"
              className="mt-2 rounded-sm h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.upLang}</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="laws-language" className="mt-2 rounded-sm h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ar">{isAr ? "العربية" : "Arabic"}</SelectItem>
                <SelectItem value="en">{isAr ? "الإنجليزية" : "English"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.upFile}</Label>
            <input
              ref={fileRef}
              type="file" required
              accept=".pdf,.txt,.md"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="laws-file"
              className="mt-2 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-slate-300 file:bg-white file:text-slate-700 hover:file:border-slate-900"
            />
          </div>
          <Button
            type="submit" disabled={uploading || !file}
            data-testid="laws-submit"
            className="rounded-sm h-12 w-full bg-slate-900 hover:bg-slate-800 text-white"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.submit}
          </Button>
        </form>

        <div className="lg:col-span-7">
          <div className="border border-slate-200 bg-white">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-slate-500" />
              <p className="eyebrow">
                {t.library} {items.length > 0 && <span className="ms-2 text-slate-400">· {items.length}</span>}
              </p>
            </div>
            {items.length === 0 ? (
              <div className="p-10 text-center text-slate-500 text-sm">{t.libraryEmpty}</div>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-[760px] overflow-auto">
                {items.map((l) => (
                  <li key={l.id} data-testid={`admin-law-row-${l.id}`} className="flex items-center justify-between gap-4 p-5">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{l.title}</p>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {l.language === "ar" ? "AR" : "EN"} · {l.filename} · {formatBytes(l.char_count)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onDelete(l.id)}
                      data-testid={`admin-law-delete-${l.id}`}
                      className="text-slate-400 hover:text-red-600 transition-colors shrink-0"
                      aria-label={t.delete}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
